// File: src/components/pages/payments/PaymentCollectionList.jsx
// Payment collection list with pagination and filters

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Calendar, User, ChevronLeft, ChevronRight, Search, Filter, X } from 'lucide-react';
import { paymentAPI, studentAPI } from '../../../services/api';
import '../../../styles/unified.css';
import './PaymentCollectionList.css';

const PaymentCollectionList = ({ refreshTrigger }) => {
  const { getAccessTokenSilently } = useAuth0();

  // State
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Filter state
  const [filters, setFilters] = useState({
    studentName: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Load payments and students
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load payments
      const paymentsResponse = await paymentAPI.getPayments(getAccessTokenSilently);
      console.log('Payments response:', paymentsResponse);

      let paymentData = [];
      if (Array.isArray(paymentsResponse)) {
        paymentData = paymentsResponse;
      } else if (paymentsResponse?.data) {
        paymentData = Array.isArray(paymentsResponse.data) 
          ? paymentsResponse.data 
          : [paymentsResponse.data];
      }

      // Add serial numbers and sort by date (newest first)
      const paymentsWithSerial = paymentData
        .sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate))
        .map((payment, index) => ({
          ...payment,
          serialNumber: index + 1
        }));

      setPayments(paymentsWithSerial);
      setFilteredPayments(paymentsWithSerial);

      // Load students for filter dropdown
      const studentsResponse = await studentAPI.getStudentsByStatus('active', getAccessTokenSilently);
      let studentData = [];
      if (Array.isArray(studentsResponse)) {
        studentData = studentsResponse;
      } else if (studentsResponse?.students) {
        studentData = studentsResponse.students;
      } else if (studentsResponse?.data) {
        studentData = studentsResponse.data;
      }

      setStudents(studentData);
      setLoading(false);
    } catch (err) {
      console.error('Error loading payments:', err);
      setError('Failed to load payments');
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshTrigger]);

  // Apply filters
  useEffect(() => {
    let filtered = [...payments];

    // Filter by student name
    if (filters.studentName) {
      filtered = filtered.filter(payment => 
        payment.studentName?.toLowerCase().includes(filters.studentName.toLowerCase())
      );
    }

    // Filter by status
    if (filters.status) {
      filtered = filtered.filter(payment => 
        payment.status === filters.status
      );
    }

    // Filter by date range
    if (filters.dateFrom) {
      filtered = filtered.filter(payment => 
        new Date(payment.paymentDate) >= new Date(filters.dateFrom)
      );
    }

    if (filters.dateTo) {
      filtered = filtered.filter(payment => 
        new Date(payment.paymentDate) <= new Date(filters.dateTo)
      );
    }

    setFilteredPayments(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [filters, payments]);

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPayments = filteredPayments.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      studentName: '',
      status: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  // Pagination handlers
  const goToPage = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `£${parseFloat(amount || 0).toFixed(2)}`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="payment-list-loading">
        <div className="loading-spinner"></div>
        <p>Loading payments...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-list-error">
        <p>{error}</p>
        <button onClick={loadData} className="btn-retry">Retry</button>
      </div>
    );
  }

  return (
    <div className="payment-collection-list">
      {/* Header with Filters Toggle */}
      <div className="payment-list-header">
        <div className="header-left">
          <h2>Payment Records</h2>
          <span className="payment-count">
            {filteredPayments.length} {filteredPayments.length === 1 ? 'payment' : 'payments'}
          </span>
        </div>
        <button 
          onClick={() => setShowFilters(!showFilters)} 
          className={`btn-filter ${showFilters ? 'active' : ''}`}
        >
          <Filter size={18} />
          Filters
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="payment-filters">
          <div className="filter-grid">
            {/* Student Name Filter */}
            <div className="filter-field">
              <label>
                <User size={16} />
                Student Name
              </label>
              <select
                value={filters.studentName}
                onChange={(e) => handleFilterChange('studentName', e.target.value)}
              >
                <option value="">All Students</option>
                {students.map(student => (
                  <option key={student.studentId} value={`${student.firstName} ${student.lastName}`}>
                    {student.firstName} {student.lastName}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div className="filter-field">
              <label>
                <Filter size={16} />
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="Claimed">Claimed</option>
                <option value="Confirmed">Confirmed</option>
                <option value="Rejected">Rejected</option>
                <option value="Adjusted">Adjusted</option>
                
              </select>
            </div>

            {/* Date From Filter */}
            <div className="filter-field">
              <label>
                <Calendar size={16} />
                From Date
              </label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </div>

            {/* Date To Filter */}
            <div className="filter-field">
              <label>
                <Calendar size={16} />
                To Date
              </label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </div>
          </div>

          {/* Clear Filters Button */}
          {(filters.studentName || filters.status || filters.dateFrom || filters.dateTo) && (
            <button onClick={clearFilters} className="btn-clear-filters">
              <X size={16} />
              Clear Filters
            </button>
          )}
        </div>
      )}

      {/* Payment Table */}
      {filteredPayments.length === 0 ? (
        <div className="payment-list-empty">
          <p>No payments found</p>
          {(filters.studentName || filters.status || filters.dateFrom || filters.dateTo) && (
            <button onClick={clearFilters} className="btn-secondary">
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="payment-table-container">
            <table className="payment-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Student Name</th>
                  <th>Payment Date</th>
                  <th>Status</th>
                  <th>Package</th>
                  <th>Classes</th>
                  <th>Original Amount</th>
                  <th>Discount</th>
                  <th>Final Amount</th>
                </tr>
              </thead>
              <tbody>
                {currentPayments.map((payment, index) => (
                  <tr key={payment.paymentId || `payment-${index}`}>
                    <td>{indexOfFirstItem + index + 1}</td>
                    <td className="student-name">{payment.studentName || '-'}</td>
                    <td>{formatDate(payment.paymentDate)}</td>
                    <td>
                      <span className={`status-badge ${payment.status?.toLowerCase()}`}>
                        {payment.status || '-'}
                      </span>
                    </td>
                    <td>
                      <span className={`package-badge ${payment.paymentOption?.toLowerCase()}`}>
                        {payment.paymentOption || '-'}
                      </span>
                    </td>
                    <td className="text-center">{payment.numberOfClasses || 0}</td>
                    <td className="amount">{formatCurrency(payment.originalAmount)}</td>
                    <td className="discount">{formatCurrency(payment.discountAmount)}</td>
                    <td className="final-amount">{formatCurrency(payment.paymentAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="payment-pagination">
              <div className="pagination-info">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredPayments.length)} of {filteredPayments.length}
              </div>

              <div className="pagination-controls">
                <button 
                  onClick={goToPreviousPage} 
                  disabled={currentPage === 1}
                  className="btn-page"
                >
                  <ChevronLeft size={18} />
                </button>

                {/* Page Numbers */}
                <div className="page-numbers">
                  {[...Array(totalPages)].map((_, index) => {
                    const pageNumber = index + 1;
                    
                    // Show first page, last page, current page, and pages around current
                    if (
                      pageNumber === 1 ||
                      pageNumber === totalPages ||
                      (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => goToPage(pageNumber)}
                          className={`btn-page-number ${currentPage === pageNumber ? 'active' : ''}`}
                        >
                          {pageNumber}
                        </button>
                      );
                    } else if (
                      pageNumber === currentPage - 2 ||
                      pageNumber === currentPage + 2
                    ) {
                      return <span key={pageNumber} className="pagination-ellipsis">...</span>;
                    }
                    return null;
                  })}
                </div>

                <button 
                  onClick={goToNextPage} 
                  disabled={currentPage === totalPages}
                  className="btn-page"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PaymentCollectionList;